import React from 'react';
import type { IntlShape } from 'react-intl';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type { ExtensionManifest, ExtensionModule } from '@atlaskit/editor-common/extensions';
import DiagramSymbolMindMapIcon from '@atlaskit/icon-lab/core/diagram-symbol-mind-map';

import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import type { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';

import CreateIcon from './whiteboard-icon.svg';
import { messages } from './messages.ts';
import { createWhiteboardQuickInsertModule } from './create-whiteboard-quick-insert.tsx';
import { slashCommands } from './slash-commands.tsx';

type CreateWhiteboardExtensionProps = {
	pageId: string;
	spaceId: string;
	spaceKey: string;
	intl: IntlShape;
	experienceTracker?: ExperienceTrackerAPI;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	flags?: FlagsStateContainer;
	contentType?: string;
	allowWhiteboardQuickInsert?: boolean;
	pageTreeStateUpdater?: PageTreeStateUpdater;
};

export const createWhiteboardExtension = ({
	pageId,
	spaceId,
	spaceKey,
	intl,
	flags,
	experienceTracker,
	createAnalyticsEvent,
	contentType,
	allowWhiteboardQuickInsert,
	pageTreeStateUpdater,
}: CreateWhiteboardExtensionProps): ExtensionManifest => {
	// empty extension if not on page
	if (contentType !== 'page') {
		return {
			title: intl.formatMessage(messages.whiteboardManifestTitle),
			description: intl.formatMessage(messages.whiteboardManifestDescription),
			type: 'com.atlassian.confluence.macro.core',
			key: 'whiteboard-extension',
			icons: {
				// eslint-disable-next-line import/dynamic-import-chunkname
				'48': () => import('@atlaskit/icon-lab/core/browser-extension'),
			},
			modules: {
				quickInsert: [],
			},
		};
	}

	const icon = async () => () => {
		if (fg('cc_fd_wb_update_diagram_icon')) {
			// Return new ADS icon when feature gate is enabled
			return <DiagramSymbolMindMapIcon label="" />;
		} else {
			return <img src={CreateIcon} alt="" />;
		}
	};

	const shouldApplyPriority = ['wb_first', 'db_first'].includes(
		expVal('cc_fd_wb_create_priority_in_slash_menu_experiment', 'cohort', 'control'),
	);

	const quickInsertCommands: ExtensionModule[] = slashCommands.map((command) => ({
		key: `create-${command.key}`,
		title: intl.formatMessage(command.title),
		description: intl.formatMessage(command.description),
		priority: shouldApplyPriority ? command.priority : undefined,
		icon,
		action: createWhiteboardQuickInsertModule(
			pageId,
			spaceId,
			spaceKey,
			experienceTracker,
			createAnalyticsEvent,
			flags,
			command.templateKey,
			pageTreeStateUpdater,
		),
		...(command.key === 'diagram' && {
			featured: true,
		}),
	}));

	return {
		title: intl.formatMessage(messages.whiteboardManifestTitle),
		description: intl.formatMessage(messages.whiteboardManifestDescription),
		type: 'com.atlassian.confluence.macro.core',
		key: 'whiteboard-extension',
		icons: { '48': icon },
		modules: {
			quickInsert: allowWhiteboardQuickInsert ? quickInsertCommands : [],
		},
	};
};
